/**
 * Batched User Fetching Manager
 * Collects user IDs and batches them into efficient Firestore queries
 */

import { debounce } from 'lodash';

export class BatchedUserFetcher {
  constructor(dispatch, batchSize = 25, delay = 100) {
    this.dispatch = dispatch;
    this.batchSize = batchSize;
    this.pendingUserIds = new Set();
    this.callbacks = new Map(); // userId -> [callbacks]

    // Debounced batch execution
    this.executeBatch = debounce(this._executeBatch.bind(this), delay);
  }

  /**
   * Request a user profile with batching
   * @param {string} userId 
   * @param {Function} callback - Called when user data is available
   */
  fetchUser(userId, callback) {
    if (!userId) return;

    // Add to pending batch
    this.pendingUserIds.add(userId);

    // Store callback
    if (!this.callbacks.has(userId)) {
      this.callbacks.set(userId, []);
    }
    if (callback) {
      this.callbacks.get(userId).push(callback);
    }

    // Trigger debounced batch execution
    this.executeBatch();
  }

  async _executeBatch() {
    if (this.pendingUserIds.size === 0) return;

    const userIds = Array.from(this.pendingUserIds);
    this.pendingUserIds.clear();

    try {
      // Import fetchUsersByIds dynamically to avoid circular deps
      const { fetchUsersByIds } = await import('../../../redux/slices/usersCacheSlice');

      // Execute batch fetch (this will populate cache for any missing users)
      const result = await this.dispatch(fetchUsersByIds({ userIds }));

      // Create a thunk to get current state (redux-thunk allows function dispatch)
      let cachedState = null;
      await this.dispatch((dispatch, getState) => {
        cachedState = getState();
      });

      // Execute callbacks for ALL requested users (including those that were already cached)
      userIds.forEach(userId => {
        const callbacks = this.callbacks.get(userId) || [];
        if (callbacks.length === 0) return;

        // First check if it's in the fresh results
        let userData = result.payload?.results?.[userId];

        // If not in results, get from Redux cache (it was already cached)
        if (!userData && cachedState?.usersCache?.entities?.[userId]?.data) {
          userData = cachedState.usersCache.entities[userId].data;
        }

        callbacks.forEach(callback => callback(userData || null));
        this.callbacks.delete(userId);
      });
    } catch (error) {
      console.error('Batch user fetch failed:', error);
      // Execute error callbacks
      userIds.forEach(userId => {
        const callbacks = this.callbacks.get(userId) || [];
        callbacks.forEach(callback => callback(null, error));
        this.callbacks.delete(userId);
      });
    }
  }

  /**
   * Clear pending requests
   */
  clear() {
    this.pendingUserIds.clear();
    this.callbacks.clear();
    this.executeBatch.cancel();
  }
}
